// BEGIN FILE: src/components/AuthPanel.tsx
import React, { useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import { TrialRequestFlow } from './TrialRequestFlow';
import PlanSelection from './PlanSelection';
import QRPaymentCapture from './QRPaymentCapture';
import { useIsMobile } from '../hooks/useIsMobile';

type Mode = 'login'|'signup'|'trial'|'paid';
type AuthType = 'phone'|'email_otp'|'email_pw';

interface AuthPanelProps {
  initialMode?: Mode;
  selectedPlan?: 'trial' | 'silver' | 'gold' | null;
  initialError?: string;
}

export default function AuthPanel({ initialMode = 'trial', selectedPlan: incomingPlan = null, initialError }: AuthPanelProps = {}) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [authType, setAuthType] = useState<AuthType>('phone'); // default to Phone OTP
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  
  // Free trial state
  const [freeTrialEmail, setFreeTrialEmail] = useState('');
  const [freeTrialSent, setFreeTrialSent] = useState(false);

  // Register fields
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regCountryCode, setRegCountryCode] = useState('IN'); // Default to India
  const [emailValid, setEmailValid] = useState<boolean | null>(null);
  const [phoneValid, setPhoneValid] = useState<boolean | null>(null);
  const [emailErrorMsg, setEmailErrorMsg] = useState('');
  const [phoneErrorMsg, setPhoneErrorMsg] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [regCompleted, setRegCompleted] = useState(false);
  const [whatsappOptIn, setWhatsappOptIn] = useState(true); // Default opted-in
  // WhatsApp OTP verification
  const [waIsWhatsApp, setWaIsWhatsApp] = useState<'yes' | 'no' | null>('yes');
  const [waOtpSent, setWaOtpSent] = useState(false);
  const [waOtpCode, setWaOtpCode] = useState('');
  const [waVerified, setWaVerified] = useState(false);
  const [waOtpLoading, setWaOtpLoading] = useState(false);
  const [waOtpError, setWaOtpError] = useState<string | null>(null);
  
  // Payment states - check localStorage first, then incoming plan from landing page
  const [selectedPlan, setSelectedPlan] = useState<'trial' | 'silver' | 'gold' | null>(() => {
    const preSelected = localStorage.getItem('preSelectedPlan') as 'trial' | 'silver' | 'gold' | null;
    return preSelected || incomingPlan;
  });
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly'); // Only yearly now
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [paymentPendingSubmitted, setPaymentPendingSubmitted] = useState(false);
  
  // New: Track if plan needs to be selected (for floating button registrations)
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [useFreeTrial, setUseFreeTrial] = useState(false);
  const [showPaymentScreen, setShowPaymentScreen] = useState(false);
  const [selectedPlanData, setSelectedPlanData] = useState<any>(null);

  // Shared email field
  const [email, setEmail] = useState('');

  // Email + Password
  const [pw, setPw] = useState('');

  // Phone OTP
  const [phone, setPhone] = useState('');   // e.g. +919876543210
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');

  // Email OTP
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');

  // Login tab — email-first flow
  interface LoginUserInfo {
    exists: boolean;
    isAdmin?: boolean;
    subscription: { tier_key: string; status: string } | null;
  }
  const [loginChecking, setLoginChecking] = useState(false);
  const [loginUserInfo, setLoginUserInfo] = useState<LoginUserInfo | null>(null);
  const [loginMagicSent, setLoginMagicSent] = useState(false);
  // Pre-fill email from initialError context so user doesn't have to re-type
  const [loginPrefillEmail] = useState('');

  // Mobile detection
  const { isMobile } = useIsMobile();
  const containerPadding = isMobile ? 16 : 24;
  const cardPadding = isMobile ? 12 : 20;
  const fontSize = {
    h1: isMobile ? 22 : 26,
    h2: isMobile ? 18 : 22,
    h3: isMobile ? 16 : 20,
    body: isMobile ? 13 : 14,
    small: isMobile ? 11 : 12,
  };

  function resetMessages() {
    setMsg(null);
  }

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const checkEmailExists = async (email: string) => {
    if (!validateEmail(email)) {
      setEmailValid(false);
      setEmailErrorMsg('Invalid email format');
      setMsg('Please enter a valid email format');
      return;
    }
    
    try {
      // Check via edge function if user already exists
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-user-exists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email: email.toLowerCase() }),
        }
      );
      
      const result = await response.json();
      
      if (result.exists) {
        setEmailValid(false);
        setEmailErrorMsg(result.message || 'Email already registered');
        setMsg(result.message);
      } else {
        setEmailValid(true);
        setEmailErrorMsg('');
        setMsg(null);
      }
    } catch (err) {
      console.error('Error checking email:', err);
      // On error, allow format validation only
      setEmailValid(true);
      setEmailErrorMsg('');
      setMsg(null);
    }
  };

  const checkPhoneExists = async (phone: string) => {
    // Basic validation - just check it's numeric and reasonable length
    if (!phone || phone.length < 10) {
      setPhoneValid(false);
      setPhoneErrorMsg('Phone number too short');
      setMsg('Please enter a valid phone number');
      return;
    }
    
    // Get country code prefix mapping
    const countryCodeMap: Record<string, string> = {
      'IN': '+91', 'US': '+1', 'GB': '+44', 'CA': '+1',
      'AU': '+61', 'NZ': '+64', 'SG': '+65', 'AE': '+971',
      'MY': '+60', 'ZA': '+27', 'OTHER': '+'
    };
    
    const prefix = countryCodeMap[regCountryCode] || '+';
    const fullPhone = `${prefix}${phone}`;
    
    try {
      // Check via edge function if phone already exists
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-user-exists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ phone: fullPhone }),
        }
      );
      
      const result = await response.json();
      
      if (result.exists) {
        setPhoneValid(false);
        setPhoneErrorMsg(result.message || 'Phone already registered');
        setMsg(result.message);
      } else {
        setPhoneValid(true);
        setPhoneErrorMsg('');
        setMsg(null);
      }
    } catch (err) {
      console.error('Error checking phone:', err);
      // On error, allow format validation only
      setPhoneValid(true);
      setPhoneErrorMsg('');
      setMsg(null);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regName || !regEmail || !regPhone || !regCountryCode) {
      setMsg('Please fill in all fields including country');
      return;
    }
    if (emailValid === false || phoneValid === false) {
      setMsg('Email or phone already registered. Please use the Login tab.');
      return;
    }
    const waBlocksSubmit = waIsWhatsApp === 'yes' && !waVerified;
    if (waBlocksSubmit) {
      setMsg('Please verify your WhatsApp number to continue.');
      return;
    }
    
    // Clear any pre-selected plan from localStorage so the plan selection screen shows fresh
    localStorage.removeItem('preSelectedPlan');
    setSelectedPlan(null);

    // Reset any pre-selected plan so plan selection screen renders correctly
    setSelectedPlan(null);
    localStorage.removeItem('preSelectedPlan');
    // Move to plan selection screen (don't send magic link yet)
    setRegCompleted(true);
    setMsg(null);
  };
  
  const sendMagicLinkForRegistration = async () => {
    setBusy(true);
    try {
      // Final check before registration
      const checkResponse = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-user-exists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email: regEmail.toLowerCase(), phone: regPhone }),
        }
      );
      
      const checkResult = await checkResponse.json();
      if (checkResult.exists) {
        setMsg(checkResult.message || 'Email or phone already registered. Please use the Login tab.');
        setEmailValid(false);
        setPhoneValid(false);
        setBusy(false);
        return;
      }
      
      // Send magic link for registration
      const { error } = await supabase.auth.signInWithOtp({
        email: regEmail.toLowerCase(),
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            full_name: regName,
            cell_number: regPhone,
            country_code: regCountryCode,
            is_new_registration: true,
            selected_plan: selectedPlan,
            use_free_trial: useFreeTrial,
            whatsapp_opted_in: waVerified,
            whatsapp_phone: `${getCountryPrefix(regCountryCode).replace('+', '')}${regPhone}`,
          },
        },
      });
      
      if (error) throw error;

      // WhatsApp opt-in stored via magic link metadata; actual update happens in App.tsx on session
      
      // Send admin notification for paid plans (Silver/Gold only, not trial)
      if (!useFreeTrial && selectedPlan && selectedPlan !== 'trial') {
        try {
          const notificationResponse = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-admin-notification`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({
                userName: regName,
                userEmail: regEmail.toLowerCase(),
                userPhone: regPhone,
                selectedPlan: selectedPlan,
                billingCycle: billingCycle,
                countryCode: regCountryCode
              }),
            }
          );
          
          if (!notificationResponse.ok) {
            console.error('Failed to send admin notification (non-blocking)');
          } else {
            console.log('Admin notification sent successfully');
          }
        } catch (notifErr) {
          // Don't block user registration if admin notification fails
          console.error('Admin notification error (non-blocking):', notifErr);
        }
      }
      
      setMagicLinkSent(true);
      if (useFreeTrial) {
        setMsg('✅ Check your email! Click the link to complete registration and activate your 7-day free trial.');
      } else if (selectedPlan) {
        setMsg(`✅ Check your email! Click the link to complete registration. Payment details for ${selectedPlan.toUpperCase()} plan will be sent separately.`);
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setMsg(err?.message || 'Registration failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleRegisterSubmitOLD = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regName || !regEmail || !regPhone || !regCountryCode) {
      setMsg('Please fill in all fields including country');
      return;
    }
    if (emailValid === false || phoneValid === false) {
      setMsg('Email or phone already registered. Please use the Login tab.');
      return;
    }
    
    setBusy(true);
    try {
      // Final check before registration
      const checkResponse = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-user-exists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email: regEmail.toLowerCase(), phone: regPhone }),
        }
      );
      
      const checkResult = await checkResponse.json();
      if (checkResult.exists) {
        setMsg(checkResult.message || 'Email or phone already registered. Please use the Login tab.');
        setEmailValid(false);
        setPhoneValid(false);
        setBusy(false);
        return;
      }
      
      // Send magic link for registration
      const { error } = await supabase.auth.signInWithOtp({
        email: regEmail.toLowerCase(),
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            full_name: regName,
            cell_number: regPhone,
            country_code: regCountryCode,
            is_new_registration: true,
          },
        },
      });
      
      if (error) throw error;
      
      setMagicLinkSent(true);
      setMsg('✅ Check your email! Click the link to complete registration and activate your 7-day free trial.');
    } catch (err: any) {
      console.error('Registration error:', err);
      setMsg(err?.message || 'Registration failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  /* ----------------- PAID PLAN REGISTRATION (PAYMENT PENDING) ----------------- */
  const handlePaidPlanRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    resetMessages();
    
    if (!regName || !regEmail || !regPhone || !regCountryCode) {
      setMsg('Please fill in all fields including country');
      return;
    }
    if (emailValid === false || phoneValid === false) {
      setMsg('Email or phone already registered. Please use the Login tab.');
      return;
    }
    if (!selectedPlan) {
      setMsg('No plan selected. Please go back and select a plan.');
      return;
    }
    
    setBusy(true);
    try {
      // Final check before registration
      const checkResponse = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-user-exists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email: regEmail.toLowerCase(), phone: regPhone }),
        }
      );
      
      const checkResult = await checkResponse.json();
      if (checkResult.exists) {
        setMsg(checkResult.message || 'Email or phone already registered. Please use the Login tab.');
        setEmailValid(false);
        setPhoneValid(false);
        setBusy(false);
        return;
      }
      
      // Get plan pricing (skip if trial)
      const planPricing: { [key: string]: number } = {
        silver: 2991,
        gold: 6996,
        trial: 0
      };
      const planAmount = planPricing[selectedPlan] || 0;
      
      // Send magic link with plan metadata
      const { error } = await supabase.auth.signInWithOtp({
        email: regEmail.toLowerCase(),
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            full_name: regName,
            cell_number: regPhone,
            country_code: regCountryCode,
            is_new_registration: true,
            payment_pending: true,
            selected_plan: selectedPlan,
            plan_amount: planAmount,
          },
        },
      });
      
      if (error) throw error;
      
      setMagicLinkSent(true);
      setPaymentPendingSubmitted(true);
      setMsg(`✅ Registration email sent! Click the link in your email to complete registration. You'll be notified at ${regEmail} when payment details are ready.`);
    } catch (err: any) {
      console.error('Paid registration error:', err);
      setMsg(err?.message || 'Registration failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  /* ----------------- PHONE OTP ----------------- */
  async function sendPhoneOtp() {
    setBusy(true); resetMessages();
    try {
      if (!phone.startsWith('+')) {
        throw new Error('Use full phone format with country code, e.g. +91…');
      }
      const { error } = await supabase.auth.signInWithOtp({
        phone,
        options: { shouldCreateUser: true }
      });
      if (error) throw error;
      setOtpSent(true);
      setMsg('OTP sent via SMS. Enter the code to complete login.');
    } catch (e:any) {
      setMsg(e?.message || 'Failed to send OTP.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyPhoneOtp() {
    setBusy(true); resetMessages();
    try {
      const { error } = await supabase.auth.verifyOtp({
        phone,
        token: otp,
        type: 'sms'
      });
      if (error) throw error;
      setMsg('Logged in!');
    } catch (e:any) {
      setMsg(e?.message || 'OTP verification failed.');
    } finally {
      setBusy(false);
    }
  }

  /* ----------------- EMAIL OTP ----------------- */
  async function sendEmailOtp() {
    setBusy(true); resetMessages();
    try {
      if (!email) throw new Error('Enter your email address.');
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          // if your project is set to Magic Link, this ensures the link returns to your app:
          emailRedirectTo: window.location.origin
        }
      });
      if (error) throw error;
      setEmailOtpSent(true);
      setMsg('We’ve emailed you a 6-digit code. If you instead receive a sign-in link, click it to log in.');
    } catch (e:any) {
      setMsg(e?.message || 'Failed to send email OTP.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyEmailOtp() {
    setBusy(true); resetMessages();
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: emailOtp,
        type: 'email' // verifies 6-digit email code
      });
      if (error) throw error;
      setMsg('Logged in!');
    } catch (e:any) {
      setMsg(e?.message || 'Email OTP verification failed.');
    } finally {
      setBusy(false);
    }
  }

  /* ----------------- PAYMENT HANDLERS ----------------- */
  const handlePayment = async () => {
    if (!selectedPlan || !regName || !regEmail || !regPhone) {
      setMsg('Missing required information');
      return;
    }

    setPaymentProcessing(true);
    setMsg(null);

    try {
      // Get plan from database
      const region = regCountryCode === 'IN' ? 'india' : 'international';
      const { data: plans, error: planError } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('plan_name', selectedPlan)
        .eq('billing_cycle', billingCycle)
        .eq('region', region)
        .eq('is_active', true)
        .single();

      if (planError || !plans) {
        throw new Error('Selected plan not found');
      }

      // Create Razorpay order via edge function
      const orderResponse = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-subscription-order`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            plan_id: plans.id,
            user_email: regEmail,
            user_details: {
              full_name: regName,
              cell_number: `${getCountryPrefix(regCountryCode)}${regPhone}`,
              country_code: regCountryCode,
            },
          }),
        }
      );

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        throw new Error(errorData.error || 'Failed to create payment order');
      }

      const { order, subscription_id } = await orderResponse.json();
      setSubscriptionId(subscription_id);

      // Open Razorpay checkout
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'ShreemAstro',
        description: `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} - ${billingCycle}`,
        order_id: order.id,
        prefill: {
          name: regName,
          email: regEmail,
          contact: `${getCountryPrefix(regCountryCode)}${regPhone}`,
        },
        theme: {
          color: '#667eea',
        },
        handler: async (response: any) => {
          try {
            // Verify payment
            const verifyResponse = await fetch(
              `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-payment`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
                },
                body: JSON.stringify({
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                  subscription_id,
                }),
              }
            );

            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }

            setPaymentCompleted(true);
            setMsg('✓ Payment successful! Now send the activation link to your email.');
          } catch (err: any) {
            setMsg(`✗ Payment verification failed: ${err.message}`);
          } finally {
            setPaymentProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPaymentProcessing(false);
            setMsg('Payment cancelled');
          },
        },
      };

      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();
    } catch (err: any) {
      console.error('Payment error:', err);
      setMsg(`✗ ${err.message}`);
      setPaymentProcessing(false);
    }
  };

  const handleSendMagicLink = async () => {
    if (!paymentCompleted || !subscriptionId) return;

    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: regEmail,
        options: {
          data: {
            full_name: regName,
            cell_number: `${getCountryPrefix(regCountryCode)}${regPhone}`,
            country_code: regCountryCode,
            subscription_id: subscriptionId,
            is_new_registration: true,
          },
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;
      setMagicLinkSent(true);
    } catch (err: any) {
      console.error('Magic link error:', err);
      setMsg(`✗ Failed to send magic link: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Fetch plan and show payment screen
  const showPaymentScreenWithPlan = async () => {
    if (!selectedPlan || selectedPlan === 'trial') {
      setMsg('Please select a plan');
      return;
    }

    setBusy(true);
    setMsg(null);

    try {
      // Fetch plan from database
      const region = regCountryCode === 'IN' ? 'india' : 'international';
      const { data: planData, error: planError } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('plan_name', selectedPlan)
        .eq('billing_cycle', billingCycle)
        .eq('region', region)
        .eq('is_active', true)
        .single();

      if (planError || !planData) {
        throw new Error('Selected plan not found. Please try again.');
      }

      setSelectedPlanData(planData);
      setShowPaymentScreen(true);
    } catch (err: any) {
      console.error('Plan fetch error:', err);
      setMsg(`✗ ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const getCountryPrefix = (code: string): string => {
    const map: Record<string, string> = {
      'IN': '+91', 'US': '+1', 'GB': '+44', 'CA': '+1',
      'AU': '+61', 'NZ': '+64', 'SG': '+65', 'AE': '+971',
      'MY': '+60', 'ZA': '+27', 'OTHER': '+'
    };
    return map[code] || '+';
  };

  /* ---- WhatsApp OTP helpers ---- */
  const getWaFullNumber = (): string => {
    return `${getCountryPrefix(regCountryCode).replace('+', '')}${regPhone}`;
  };

  const handleSendWaOtp = async () => {
    const phone = getWaFullNumber();
    if (!phone || phone.replace(/\D/g, '').length < 7) {
      setWaOtpError('Please enter a valid phone number first.');
      return;
    }
    setWaOtpLoading(true);
    setWaOtpError(null);
    try {
      const { data, error } = await supabase.functions.invoke('whatsapp-otp', {
        body: { action: 'send', phone },
      });
      if (error || data?.error) {
        setWaOtpError(data?.error ?? error?.message ?? 'Failed to send OTP. Please try again.');
      } else {
        setWaOtpSent(true);
        setWaOtpCode('');
      }
    } catch (e: any) {
      setWaOtpError(e.message ?? 'Failed to send OTP. Please try again.');
    }
    setWaOtpLoading(false);
  };

  const handleVerifyWaOtp = async () => {
    setWaOtpLoading(true);
    setWaOtpError(null);
    try {
      const { data, error } = await supabase.functions.invoke('whatsapp-otp', {
        body: { action: 'verify', phone: getWaFullNumber(), code: waOtpCode },
      });
      if (error || data?.error) {
        setWaOtpError(data?.error ?? error?.message ?? 'Verification failed.');
      } else if (data?.verified) {
        setWaVerified(true);
      } else {
        setWaOtpError(data?.error ?? 'Incorrect code. Please try again.');
      }
    } catch (e: any) {
      setWaOtpError(e.message ?? 'Verification failed.');
    }
    setWaOtpLoading(false);
  };

  /* ----------------- GOOGLE OAUTH SIGN-IN ----------------- */
  // Login tab — check email existence + subscription before showing login options
  const checkLoginEmail = async () => {
    if (!email || !validateEmail(email)) {
      setMsg('Please enter a valid email address.');
      return;
    }
    setLoginChecking(true);
    setLoginUserInfo(null);
    setMsg(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-user-exists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email: email.toLowerCase() }),
        }
      );
      const result = await response.json();
      setLoginUserInfo({
        exists: !!result.exists,
        isAdmin: !!result.isAdmin,
        subscription: result.subscription || null,
      });
    } catch (err) {
      setMsg('Could not check email. Please try again.');
    } finally {
      setLoginChecking(false);
    }
  };

  // Send a plain magic link (no registration metadata) to an existing user
  const sendLoginMagicLink = async () => {
    if (!email) return;
    setBusy(true);
    setMsg(null);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: window.location.origin,
        },
      });
      if (error) throw error;
      setLoginMagicSent(true);
    } catch (e: any) {
      setMsg(e?.message || 'Failed to send link. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setBusy(true);
    resetMessages();
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent', // Force consent screen to ensure email sharing permission
          },
        },
      });

      if (error) throw error;
      
      // User will be redirected to Google consent screen
      // After approval, they'll be redirected back to the app
      // Session will be automatically created by Supabase
    } catch (err: any) {
      console.error('Google OAuth error:', err);
      setMsg(`✗ Google sign-in failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  /* ----------------- FREE TRIAL REGISTRATION (No Payment) ----------------- */
  const handleFreeTrialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    resetMessages();
    
    try {
      if (!freeTrialEmail || !validateEmail(freeTrialEmail)) {
        throw new Error('Please enter a valid email address');
      }
      
      console.log('[Free Trial] Starting registration for:', freeTrialEmail);
      
      // Check if email already exists
      const checkResponse = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-user-exists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email: freeTrialEmail.toLowerCase() }),
        }
      );
      
      const result = await checkResponse.json();
      
      if (result.exists) {
        throw new Error(result.message || 'Email already registered. Please use the Login tab.');
      }
      
      // Get free trial plan ID from database (try both regions)
      let freePlan = null;
      
      // Try India region first
      const { data: indiaPlan, error: indiaError } = await supabase
        .from('subscription_plans')
        .select('id')
        .eq('plan_name', 'free_trial')
        .eq('region', 'india')
        .eq('is_active', true)
        .maybeSingle();
      
      if (indiaPlan) {
        freePlan = indiaPlan;
        console.log('[Free Trial] Found India plan:', indiaPlan.id);
      } else {
        console.warn('[Free Trial] India plan not found:', indiaError);
        
        // Fallback to international region
        const { data: intlPlan, error: intlError } = await supabase
          .from('subscription_plans')
          .select('id')
          .eq('plan_name', 'free_trial')
          .eq('region', 'international')
          .eq('is_active', true)
          .maybeSingle();
        
        if (intlPlan) {
          freePlan = intlPlan;
          console.log('[Free Trial] Using international plan:', intlPlan.id);
        } else {
          console.error('[Free Trial] No free trial plan found in any region:', intlError);
          throw new Error('Free trial is temporarily unavailable. Please try the Login tab if you already have an account, or contact support.');
        }
      }
      
      if (!freePlan) {
        throw new Error('Free trial plan not found. Please contact support.');
      }
      
      console.log('[Free Trial] Sending magic link with plan ID:', freePlan.id);
      
      // Send magic link with free trial metadata
      const { data, error } = await supabase.auth.signInWithOtp({
        email: freeTrialEmail.toLowerCase(),
        options: {
          data: {
            is_new_registration: true,
            is_free_trial: true,
            free_trial_plan_id: freePlan.id,
          },
          emailRedirectTo: `${window.location.origin}/`,
          shouldCreateUser: true,
        },
      });
      
      if (error) {
        console.error('[Free Trial] Magic link error:', error);
        
        // Check for rate limit errors
        if (error.message?.includes('rate') || error.message?.includes('limit') || error.message?.includes('too many')) {
          throw new Error('⏱️ Too many requests. Please wait 5 minutes and try again, or contact admin for immediate access.');
        }
        
        throw error;
      }
      
      console.log('[Free Trial] Magic link sent successfully');
      
      setFreeTrialSent(true);
      setMsg('✓ Magic link sent! Check your email (and spam folder).');
    } catch (err: any) {
      console.error('[Free Trial] Error:', err);
      setMsg(`✗ ${err.message || 'Failed to send magic link. Please try again.'}`);
    } finally {
      setBusy(false);
    }
  };

  /* ----------------- EMAIL + PASSWORD ----------------- */
  async function submitEmailPw() {
    setBusy(true); resetMessages();
    try {
      if (mode === 'signup') {
        const { error } = await supabase.auth.signUp({ 
          email, 
          password: pw,
          options: {
            emailRedirectTo: window.location.origin
          }
        });
        if (error) throw error;
        setMsg('✅ Signup successful! Please check your email for a confirmation link.');
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password: pw });
        if (error) {
          // Check if it's email not confirmed error
          if (error.message?.toLowerCase().includes('email not confirmed')) {
            throw new Error('Please confirm your email address before logging in. Check your inbox for the confirmation link.');
          }
          throw error;
        }
        // Check if email is verified
        if (data.user && !data.user.email_confirmed_at) {
          setMsg('⚠️ Please verify your email address. Check your inbox for the confirmation link.');
          await supabase.auth.signOut();
          return;
        }
        setMsg('Logged in!');
      }
    } catch (e:any) {
      setMsg(e?.message || 'Email/password auth failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ 
      maxWidth: 600, 
      margin: '0 auto', 
      padding: containerPadding, 
      border: '1px solid #eee', 
      borderRadius: isMobile ? 8 : 12, 
      background: 'linear-gradient(135deg, #fffafa 0%, #fff5f5 30%, #fffef8 70%, #fffff8 100%)',
      boxShadow: isMobile ? '0 4px 16px rgba(139, 0, 0, 0.1)' : '0 8px 32px rgba(139, 0, 0, 0.12)',
      position: 'relative',
      overflow: 'visible'
    }}>
      {/* Subtle celestial background pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(circle at 20% 30%, rgba(139, 0, 0, 0.025) 0%, transparent 50%),
          radial-gradient(circle at 80% 70%, rgba(220, 20, 60, 0.02) 0%, transparent 50%),
          radial-gradient(circle at 50% 50%, rgba(218, 165, 32, 0.015) 0%, transparent 50%)
        `,
        backgroundSize: '800px 800px, 600px 600px, 1000px 1000px',
        opacity: 0.5,
        pointerEvents: 'none',
        zIndex: 0
      }} />
      
      {/* Zodiac symbols watermark */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '5%',
        fontSize: '120px',
        opacity: 0.02,
        color: '#8B0000',
        fontFamily: 'serif',
        pointerEvents: 'none',
        zIndex: 0,
        transform: 'rotate(15deg)'
      }}>♈</div>
      
      <div style={{
        position: 'absolute',
        bottom: '15%',
        left: '8%',
        fontSize: '100px',
        opacity: 0.02,
        color: '#DAA520',
        fontFamily: 'serif',
        pointerEvents: 'none',
        zIndex: 0,
        transform: 'rotate(-20deg)'
      }}>☽</div>
      
      {/* Content wrapper */}
      <div style={{ position: 'relative', zIndex: 1 }}>
      {/* Logo and Title */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <img src="/Logo.png" alt="ShreemAstro" style={{ width: 80, height: 80, marginBottom: 16, boxShadow: '0 4px 12px rgba(139,0,0,0.15)' }} />
        <h2 style={{ fontWeight: 700, fontSize: '1.75rem', color: '#8B0000', margin: 0, lineHeight: 1.2, fontFamily: 'Amita, serif' }}>ShreemAstro<sup style={{ fontSize: '0.6em', fontWeight: 400, verticalAlign: 'super', lineHeight: 0, color: 'rgba(184,48,80,1)' }}>™</sup></h2>
        <p style={{ color: '#666', fontSize: 14, margin: '8px 0 0 0' }}>Ancient Wisdom Powered by Intelligent Tech</p>
      </div>

      {/* Tab buttons - Simplified to 2 tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:20, borderBottom:'2px solid #eee', paddingBottom:8 }}>
        <button onClick={()=>{ setMode('trial'); resetMessages(); setFreeTrialSent(false); }} style={{ flex: 1, padding:'12px', borderRadius:'8px 8px 0 0', border:'none', background: (mode==='trial' || mode==='paid') ? 'linear-gradient(135deg, #8B0000 0%, #DC143C 100%)' : 'transparent', color: (mode==='trial' || mode==='paid') ? '#fff' : '#666', fontWeight: (mode==='trial' || mode==='paid') ? 'bold' : 'normal', cursor:'pointer', fontSize: 13 }}>🎯 Plans</button>
        <button onClick={()=>{ setMode('login'); resetMessages(); }} style={{ flex: 1, padding:'12px', borderRadius:'8px 8px 0 0', border:'none', background: mode==='login' ? 'linear-gradient(135deg, #8B0000 0%, #DC143C 100%)' : 'transparent', color: mode==='login' ? '#fff' : '#666', fontWeight: mode==='login' ? 'bold' : 'normal', cursor:'pointer', fontSize: 13 }}>🔐 Login</button>
      </div>

      {/* UNIFIED PLANS TAB - Step 1: Registration Form First */}
      {(mode === 'trial' || mode === 'paid') && !magicLinkSent && !regCompleted && (
        <div style={{ marginTop: -20, padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 20, color: '#8B0000', textAlign: 'center' }}>
            Start Your Registration
          </h3>
          
          <div style={{
            background: '#fffafa',
            padding: 16,
            borderRadius: 8,
            marginBottom: 20,
            fontSize: 13,
            lineHeight: 1.6,
            textAlign: 'center',
            border: '1px solid rgba(139, 0, 0, 0.3)'
          }}>
            💡 Complete your details first, then choose plans from 7-Day-Trial / Silver / Gold
          </div>

          <form onSubmit={handleRegisterSubmit}>
            <label style={{ display:'block', fontSize:13, opacity:0.9, marginBottom:4 }}>Full Name *</label>
            <input 
              type="text" 
              value={regName} 
              onChange={e=>setRegName(e.target.value)} 
              placeholder="John Doe" 
              style={{ width:'100%', margin:'0 0 16px 0', padding:12, border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' }} 
              required 
            />
            
            <label style={{ display:'block', fontSize:13, opacity:0.9, marginBottom:4 }}>Email *</label>
            <div style={{ position:'relative', marginBottom:16 }}>
              <input 
                type="email" 
                value={regEmail} 
                onChange={e=>{setRegEmail(e.target.value); setEmailValid(null); setEmailErrorMsg(''); resetMessages();}} 
                onBlur={()=> regEmail && checkEmailExists(regEmail)} 
                placeholder="you@example.com" 
                style={{ 
                  width:'100%', 
                  padding:12, 
                  paddingRight:40, 
                  border: emailValid === false ? '2px solid #ef4444' : emailValid === true ? '2px solid #8B0000' : '1px solid #ddd', 
                  borderRadius:8, 
                  fontSize:14, 
                  boxSizing:'border-box' 
                }} 
                required 
              />
              {emailValid === true && <span style={{position:'absolute',right:12,top:12,color:'#8B0000',fontSize:20}}>✓</span>}
              {emailValid === false && <span style={{position:'absolute',right:12,top:12,color:'#ef4444',fontSize:20}}>✗</span>}
            </div>
            {emailValid === true && <div style={{color:'#8B0000',fontSize:12,marginTop:-12,marginBottom:12}}>✓ Email available</div>}
            {emailValid === false && <div style={{color:'#ef4444',fontSize:12,marginTop:-12,marginBottom:12}}>✗ {emailErrorMsg}</div>}

            <label style={{ display:'block', fontSize:13, opacity:0.9, marginBottom:4 }}>Phone Number *</label>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              <select 
                value={regCountryCode} 
                onChange={e=>setRegCountryCode(e.target.value)} 
                style={{ padding:12, border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', background:'#fff', minWidth:110 }} 
                required
              >
                <option value="IN">🇮🇳 +91</option>
                <option value="US">🇺🇸 +1</option>
                <option value="GB">🇬🇧 +44</option>
                <option value="CA">🇨🇦 +1</option>
                <option value="AU">🇦🇺 +61</option>
                <option value="NZ">🇳🇿 +64</option>
                <option value="SG">🇸🇬 +65</option>
                <option value="AE">🇦🇪 +971</option>
                <option value="MY">🇲🇾 +60</option>
                <option value="ZA">🇿🇦 +27</option>
                <option value="OTHER">🌍 Other</option>
              </select>
              <div style={{ position:'relative', flex:1 }}>
                <input 
                  type="tel" 
                  value={regPhone} 
                  onChange={e=>{setRegPhone(e.target.value); setPhoneValid(null); setPhoneErrorMsg(''); resetMessages();}} 
                  onBlur={()=> regPhone && checkPhoneExists(regPhone)} 
                  placeholder="9876543210" 
                  style={{ 
                    width:'100%', 
                    padding:12, 
                    paddingRight:40, 
                    border: phoneValid === false ? '2px solid #ef4444' : phoneValid === true ? '2px solid #8B0000' : '1px solid #ddd', 
                    borderRadius:8, 
                    fontSize:14, 
                    boxSizing:'border-box' 
                  }} 
                  required 
                />
                {phoneValid === true && <span style={{position:'absolute',right:12,top:12,color:'#8B0000',fontSize:20}}>✓</span>}
                {phoneValid === false && <span style={{position:'absolute',right:12,top:12,color:'#ef4444',fontSize:20}}>✗</span>}
              </div>
            </div>
            {phoneValid === true && <div style={{color:'#8B0000',fontSize:12,marginTop:-12,marginBottom:12}}>✓ Phone available</div>}
            {phoneValid === false && <div style={{color:'#ef4444',fontSize:12,marginTop:-12,marginBottom:12}}>✗ {phoneErrorMsg}</div>}

            {/* WhatsApp Opt-In */}
            <div style={{ margin: '8px 0 16px 0', padding: '14px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>

              {/* Benefits */}
              <div style={{ marginBottom: 12, padding: '10px 12px', background: '#dcfce7', borderRadius: 6, border: '1px solid #86efac' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#14532d', marginBottom: 6 }}>🔔 Get WhatsApp alerts on {regPhone || 'your number'}:</div>
                <div style={{ fontSize: 12, color: '#166534', lineHeight: 1.7 }}>
                  ✦ <strong>Weekly predictions</strong> based on your current dasha period<br />
                  ✦ <strong>Retrograde &amp; eclipse alerts</strong> for planets affecting your chart<br />
                  ✦ <strong>Personalised timing guidance</strong> for important life decisions
                </div>
              </div>

              {/* Opt-in / Opt-out toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>
                  {waIsWhatsApp === 'yes' ? '📲 Yes, send me WhatsApp alerts' : '🔕 No thanks, skip for now'}
                </div>
                <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: 46, height: 26, cursor: 'pointer', flexShrink: 0 }}>
                  <input
                    type="checkbox"
                    checked={waIsWhatsApp === 'yes'}
                    onChange={e => {
                      setWaIsWhatsApp(e.target.checked ? 'yes' : 'no');
                      setWaVerified(false); setWaOtpSent(false); setWaOtpCode(''); setWaOtpError(null);
                    }}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                  />
                  <span style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    background: waIsWhatsApp === 'yes' ? '#16a34a' : '#d1d5db',
                    borderRadius: 13, transition: 'background 0.25s'
                  }} />
                  <span style={{
                    position: 'absolute',
                    width: 20, height: 20, top: 3,
                    left: waIsWhatsApp === 'yes' ? 23 : 3,
                    background: '#fff', borderRadius: '50%',
                    transition: 'left 0.25s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
                  }} />
                </label>
              </div>

              {/* OTP verification — only when opted in */}
              {waIsWhatsApp === 'yes' && !waVerified && (
                <div style={{ marginBottom: 8 }}>
                  {!waOtpSent ? (
                    <button
                      type="button"
                      onClick={handleSendWaOtp}
                      disabled={waOtpLoading || !regPhone}
                      style={{ width: '100%', padding: '8px 0', borderRadius: 6, border: 'none', background: (waOtpLoading || !regPhone) ? '#86efac' : '#16a34a', color: '#fff', fontSize: 13, fontWeight: 600, cursor: (waOtpLoading || !regPhone) ? 'not-allowed' : 'pointer' }}
                    >
                      {waOtpLoading ? 'Sending…' : '📲 Send OTP to WhatsApp'}
                    </button>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={waOtpCode}
                          onChange={e => setWaOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="6-digit code"
                          maxLength={6}
                          style={{ flex: 1, padding: '8px 10px', border: '1px solid #86efac', borderRadius: 6, fontSize: 18, letterSpacing: 6, boxSizing: 'border-box', textAlign: 'center', background: '#fff' }}
                        />
                        <button
                          type="button"
                          onClick={handleVerifyWaOtp}
                          disabled={waOtpLoading || waOtpCode.length !== 6}
                          style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: (waOtpLoading || waOtpCode.length !== 6) ? '#86efac' : '#16a34a', color: '#fff', fontSize: 13, fontWeight: 600, cursor: (waOtpLoading || waOtpCode.length !== 6) ? 'not-allowed' : 'pointer' }}
                        >
                          {waOtpLoading ? '…' : 'Verify'}
                        </button>
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        Didn't receive it?{' '}
                        <span onClick={handleSendWaOtp} style={{ color: '#16a34a', cursor: 'pointer', textDecoration: 'underline' }}>Resend OTP</span>
                      </div>
                    </div>
                  )}
                  {waOtpError && <div style={{ marginTop: 6, fontSize: 11, color: '#ef4444' }}>{waOtpError}</div>}
                </div>
              )}

              {waVerified && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <span style={{ fontSize: 20 }}>✅</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>WhatsApp verified!</div>
                    <div style={{ fontSize: 11, color: '#166534' }}>You'll receive weekly digests &amp; dasha alerts on {regPhone}.</div>
                  </div>
                </div>
              )}

            </div>

            <button
              type="submit"
              disabled={busy || !regName || !regEmail || !regPhone || !regCountryCode || emailValid === false || phoneValid === false || (waIsWhatsApp === 'yes' && !waVerified)}
              style={{
                width:'100%',
                padding:14,
                borderRadius:8,
                border:'none',
                background: (busy || emailValid === false || phoneValid === false || (waIsWhatsApp === 'yes' && !waVerified)) ? '#ccc' : 'linear-gradient(135deg, #8B0000 0%, #DC143C 100%)',
                color:'#fff',
                fontWeight:'bold',
                cursor: (busy || emailValid === false || phoneValid === false || (waIsWhatsApp === 'yes' && !waVerified)) ? 'not-allowed' : 'pointer',
                fontSize:15,
                marginTop: 8
              }}
            >
              {busy ? 'Processing...' : 'Continue to Select Plan'}
            </button>
          </form>
        </div>
      )}

      {/* UNIFIED PLANS TAB - Step 2: Plan Selection (After Registration) */}
      {(mode === 'trial' || mode === 'paid') && !magicLinkSent && regCompleted && !selectedPlan && !showPlanSelection && (
        <div style={{ marginTop: -20, padding: '20px' }}>
          {/* User Details Summary */}
          <div style={{ background: '#f0f9ff', padding: 12, borderRadius: 8, marginBottom: 24, fontSize: 13, lineHeight: 1.6 }}>
            <strong>{regName}</strong> • {regEmail} • {regPhone}
          </div>

          <h3 style={{ margin: '0 0 20px 0', fontSize: 20, color: '#8B0000', textAlign: 'center' }}>
            Choose Your Plan
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 24 }}>
            {/* 7-Day Trial Card */}
            <div 
              onClick={() => { 
                setSelectedPlan('trial'); 
                localStorage.setItem('preSelectedPlan', 'trial');
                sendMagicLinkForRegistration(); // Send magic link directly for trial
              }}
              style={{
                border: '3px solid #DAA520',
                borderRadius: isMobile ? 8 : 12,
                padding: cardPadding,
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #fffef8 0%, #fff9f0 100%)',
                transition: 'all 0.2s',
                position: 'relative'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(218, 165, 32, 0.2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ position: 'absolute', top: -12, right: isMobile ? 12 : 20, background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)', color: '#fff', padding: '4px 12px', borderRadius: 4, fontSize: fontSize.small, fontWeight: 'bold' }}>
                🎁 TRY FREE
              </div>
              <div style={{ fontSize: fontSize.h2, fontWeight: 'bold', color: '#B8860B', marginBottom: 4 }}>7-Day Trial</div>
              <div style={{ fontSize: isMobile ? 24 : 28, fontWeight: 'bold', color: '#B8860B', marginBottom: 12 }}>
                ₹0 <span style={{ fontSize: fontSize.body, fontWeight: 'normal', opacity: 0.8 }}>/ 7 days</span>
              </div>
              <div style={{ fontSize: fontSize.body, color: '#8B6914', lineHeight: 1.8, marginBottom: 16 }}>
                ✓ Self & Spouse + 2 family (4 charts)<br/>
                ✓ Life Areas Dashboard & Remedies<br/>
                ✓ Proactive Daily/Weekly Forecast<br/>
                ✗ <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>AI chat (not available)</span><br/>
                ✓ Yogas, predictions & divisional charts<br/>
                ✓ Future Event Predictor
              </div>
              <button 
                style={{
                  width: '100%',
                  padding: isMobile ? 10 : 12,
                  borderRadius: 8,
                  border: 'none',
                  background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: fontSize.body + 1
                }}
              >
                Start Free Trial
              </button>
            </div>

            {/* Silver Plan Card */}
            <div 
              onClick={() => { 
                setSelectedPlan('silver'); 
                localStorage.setItem('preSelectedPlan', 'silver');
                setShowPlanSelection(true); // Show payment choice screen
              }}
              style={{
                border: '2px solid #C0C0C0',
                borderRadius: isMobile ? 8 : 12,
                padding: cardPadding,
                cursor: 'pointer',
                background: '#fff',
                transition: 'all 0.2s'
              }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(192, 192, 192, 0.2)'; e.currentTarget.style.borderColor = '#C0C0C0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#C0C0C0'; }}
                >
                  <div style={{ fontSize: fontSize.h2, fontWeight: 'bold', color: '#C0C0C0', marginBottom: 4 }}>Silver</div>
                  <div style={{ fontSize: isMobile ? 24 : 28, fontWeight: 'bold', color: '#808080', marginBottom: 12 }}>
                    ₹2,991 <span style={{ fontSize: fontSize.body, fontWeight: 'normal', opacity: 0.8 }}>/ year</span>
                  </div>
                  <div style={{ fontSize: fontSize.body, color: '#666', lineHeight: 1.8, marginBottom: 16 }}>
                    ✓ Self & Spouse + 2 family (4 charts)<br/>
                    ✓ 3 AI chat questions/month<br/>
                    ✓ Life Areas Dashboard & Remedies<br/>
                    ✓ Proactive Daily/Weekly Forecast<br/>
                    ✓ Yogas, predictions & divisional charts<br/>
                    ✓ Future Event Predictor
                  </div>
                  <button 
                    style={{
                      width: '100%',
                      padding: isMobile ? 10 : 12,
                      borderRadius: 8,
                      border: 'none',
                      background: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)',
                      color: '#fff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: fontSize.body + 1
                    }}
                  >
                    Select Silver
                  </button>
                </div>

                {/* Gold Plan Card */}
                <div 
                  onClick={() => { 
                    setSelectedPlan('gold'); 
                    localStorage.setItem('preSelectedPlan', 'gold');
                    setShowPlanSelection(true); // Show payment choice screen
                  }}
                  style={{
                    border: '3px solid #DAA520',
                    borderRadius: isMobile ? 8 : 12,
                    padding: cardPadding,
                    cursor: 'pointer',
                    background: 'linear-gradient(135deg, #fffbf0 0%, #fff5e6 100%)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(218, 165, 32, 0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ position: 'absolute', top: -12, right: isMobile ? 12 : 20, background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)', color: '#fff', padding: '4px 12px', borderRadius: 4, fontSize: fontSize.small, fontWeight: 'bold' }}>
                    ⭐ POPULAR
                  </div>
                  <div style={{ fontSize: fontSize.h2, fontWeight: 'bold', color: '#DAA520', marginBottom: 4 }}>Gold</div>
                  <div style={{ fontSize: isMobile ? 24 : 28, fontWeight: 'bold', color: '#B8860B', marginBottom: 12 }}>
                    ₹6,996 <span style={{ fontSize: fontSize.body, fontWeight: 'normal', opacity: 0.8 }}>/ year</span>
                  </div>
                  <div style={{ fontSize: fontSize.body, color: '#8B6914', lineHeight: 1.8, marginBottom: 16 }}>
                    <strong>All of Silver +</strong><br/>
                    ✓ Self & Spouse + 4 family (6 charts)<br/>
                    ✓ 6 AI chat questions/month<br/>
                    ✓ Marriage Compatibility Analysis<br/>
                    ✓ Personal Muhurat Advisor<br/>
                    ✓ Life Event Finder
                  </div>
                  <button 
                    style={{
                      width: '100%',
                      padding: isMobile ? 10 : 12,
                      borderRadius: 8,
                      border: 'none',
                      background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                      color: '#fff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: fontSize.body + 1
                    }}
                  >
                    Select Gold
                  </button>
                </div>
              </div>
        </div>
      )}

      {/* PAYMENT CHOICE SCREEN (Silver/Gold only - after registration) */}
      {(mode === 'trial' || mode === 'paid') && showPlanSelection && selectedPlan && selectedPlan !== 'trial' && !magicLinkSent && (
        <div style={{ padding: cardPadding }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: fontSize.h3, color: '#667eea', textAlign: 'center' }}>
            How would you like to proceed?
          </h3>
          
          {/* User Details Summary */}
          <div style={{ background: '#f0f9ff', padding: isMobile ? 10 : 12, borderRadius: 8, marginBottom: isMobile ? 16 : 24, fontSize: fontSize.body, lineHeight: 1.6 }}>
            <strong>{regName}</strong> • {regEmail} • {regPhone}<br/>
            Plan: <strong>{selectedPlan === 'silver' ? 'Silver' : 'Gold'}</strong> (₹{selectedPlan === 'silver' ? '2,991' : '6,996'}/year)
          </div>

          {/* Option A: Proceed with Payment */}
          <div 
            onClick={() => { 
              setUseFreeTrial(false); 
              showPaymentScreenWithPlan(); 
            }}
            style={{
              border: '2px solid #667eea',
              borderRadius: isMobile ? 8 : 12,
              padding: cardPadding,
              marginBottom: 16,
              cursor: 'pointer',
              background: '#f0f9ff',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ fontSize: fontSize.h3, fontWeight: 'bold', marginBottom: 8, color: '#667eea' }}>
              🅰️ Proceed with Payment
            </div>
            <div style={{ fontSize: fontSize.body, color: '#666', lineHeight: 1.6, marginBottom: 12 }}>
              • Payment link will be emailed to you<br/>
              • Plan activates after successful payment<br/>
              • Full access to all features
            </div>
            <button 
              onClick={(e) => { 
                e.stopPropagation(); 
                setUseFreeTrial(false); 
                showPaymentScreenWithPlan(); 
              }}
              style={{
                width: '100%',
                padding: isMobile ? 10 : 12,
                borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: fontSize.body + 1
              }}
            >
              Continue - Get Payment Link
            </button>
          </div>

          {/* Option B: Start with Free Trial */}
          <div 
            onClick={() => { 
              setUseFreeTrial(true); 
              sendMagicLinkForRegistration(); 
            }}
            style={{
              border: '3px solid #22C55E',
              borderRadius: isMobile ? 8 : 12,
              padding: cardPadding,
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              transition: 'all 0.2s',
              position: 'relative'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ position: 'absolute', top: -12, right: isMobile ? 12 : 20, background: '#22C55E', color: '#fff', padding: '4px 12px', borderRadius: 4, fontSize: fontSize.small, fontWeight: 'bold' }}>
              ⭐ RECOMMENDED
            </div>
            <div style={{ fontSize: fontSize.h3, fontWeight: 'bold', marginBottom: 8, color: '#16A34A' }}>
              🅱️ Start with 7-Day Trial First
            </div>
            <div style={{ fontSize: fontSize.body, color: '#166534', lineHeight: 1.6, marginBottom: 12 }}>
              • Get immediate access (no payment wait)<br/>
              • Try all features for 7 days<br/>
              • Payment link sent separately for later<br/>
              • Upgrade anytime within trial period
            </div>
            <button 
              onClick={(e) => { 
                e.stopPropagation(); 
                setUseFreeTrial(true); 
                sendMagicLinkForRegistration(); 
              }}
              style={{
                width: '100%',
                padding: isMobile ? 10 : 12,
                borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: 15
              }}
            >
              Start Free Trial - Pay Later
            </button>
          </div>

          {/* Back button */}
          <button 
            onClick={() => { 
              setShowPlanSelection(false); 
              setRegCompleted(false); 
              setMsg(null); 
            }}
            style={{
              width: '100%',
              padding: 10,
              borderRadius: 8,
              border: '1px solid #ddd',
              background: '#fff',
              cursor: 'pointer',
              fontSize: fontSize.body,
              color: '#666',
              marginTop: 16
            }}
          >
            ← Back to Registration
          </button>
        </div>
      )}

      {/* QR PAYMENT CAPTURE SCREEN */}
      {showPaymentScreen && selectedPlanData && !magicLinkSent && (
        <QRPaymentCapture
          plan={selectedPlanData}
          userEmail={regEmail}
          userDetails={{
            full_name: regName,
            cell_number: `${getCountryPrefix(regCountryCode)}${regPhone}`,
            country_code: regCountryCode,
          }}
          region={regCountryCode === 'IN' ? 'india' : 'international'}
          onSuccess={async (subId: string) => {
            setSubscriptionId(subId);
            
            // Send magic link for registration
            try {
              setBusy(true);
              const { error } = await supabase.auth.signInWithOtp({
                email: regEmail,
                options: {
                  data: {
                    full_name: regName,
                    cell_number: `${getCountryPrefix(regCountryCode)}${regPhone}`,
                    country_code: regCountryCode,
                    subscription_id: subId,
                    is_new_registration: true,
                  },
                  emailRedirectTo: `${window.location.origin}/`,
                },
              });

              if (error) throw error;
              
              setMagicLinkSent(true);
              setShowPaymentScreen(false);
            } catch (err: any) {
              console.error('Magic link error:', err);
              setMsg(`✗ Payment successful but failed to send magic link: ${err.message}`);
            } finally {
              setBusy(false);
            }
          }}
          onBack={() => {
            setShowPaymentScreen(false);
            setSelectedPlanData(null);
          }}
        />
      )}

      {/* Thank You Message After Submission */}
      {(mode === 'trial' || mode === 'paid') && magicLinkSent && (
        <div style={{ padding: isMobile ? '20px 12px' : '30px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: isMobile ? 48 : 64, marginBottom: 16 }}>🎉</div>
          <h2 style={{ margin: 0, marginBottom: 12, fontSize: isMobile ? 20 : 24, color: selectedPlan === 'trial' ? '#16A34A' : '#667eea' }}>
            {selectedPlan === 'trial' ? 'Free Trial Activated!' : 'Thank You!'}
          </h2>
          <div style={{
            background: '#f0fdf4',
            border: '2px solid #22c55e',
            borderRadius: 12,
            padding: 24,
            marginBottom: 20
          }}>
            {selectedPlan === 'trial' ? (
              <>
                <p style={{ margin: '0 0 16px 0', fontSize: 15, lineHeight: 1.6 }}>
                  Your <strong>7-Day Free Trial</strong> is ready!
                </p>
                <p style={{ margin: '0 0 16px 0', fontSize: 15, lineHeight: 1.6, fontWeight: 'bold' }}>
                  Check your email to activate your trial.
                </p>
              </>
            ) : useFreeTrial ? (
              <>
                <p style={{ margin: '0 0 16px 0', fontSize: 15, lineHeight: 1.6 }}>
                  Your <strong>7-Day Trial</strong> is ready! Payment link for <strong>{selectedPlan ? (selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)) : 'Unknown'} Plan</strong> sent separately.
                </p>
                <p style={{ margin: '0 0 16px 0', fontSize: 15, lineHeight: 1.6, fontWeight: 'bold' }}>
                  Start using immediately - upgrade anytime within 7 days.
                </p>
              </>
            ) : (
              <>
                <p style={{ margin: '0 0 16px 0', fontSize: 15, lineHeight: 1.6 }}>
                  Your request for <strong>{selectedPlan ? (selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)) : 'Unknown'} Plan</strong> (₹{selectedPlan === 'silver' ? '2,991' : '6,996'}/year) has been received.
                </p>
                <p style={{ margin: '0 0 16px 0', fontSize: 15, lineHeight: 1.6, fontWeight: 'bold' }}>
                  Payment link will be sent via email shortly.
                </p>
              </>
            )}
            <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
              Please check: <strong>{regEmail}</strong>
            </p>
          </div>
          
          <div style={{
            padding: 16,
            background: '#fff7ed',
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.6,
            marginBottom: 16
          }}>
            📧 <strong>Check your inbox</strong><br/>
            Click the verification link in your email to complete {selectedPlan === 'trial' ? 'trial activation' : 'registration'}.
          </div>

          <div style={{ fontSize: 12, opacity: 0.7 }}>
            Questions? Email us at support@shreemastro.com
          </div>
        </div>
      )}

      {/* HIDE OLD RAZORPAY PAYMENT FLOW - Keeping code for future use */}
      {false && mode === 'signup' && regCompleted && !magicLinkSent && (
        <form onSubmit={handleRegisterSubmit}>
          <label style={{ display:'block', fontSize:13, opacity:0.9, marginBottom:4 }}>Full Name *</label>
          <input type="text" value={regName} onChange={e=>setRegName(e.target.value)} placeholder="John Doe" style={{ width:'100%', margin:'0 0 16px 0', padding:10, border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' }} required />
          
          <label style={{ display:'block', fontSize:13, opacity:0.9, marginBottom:4 }}>Email *</label>
          <div style={{ position:'relative', marginBottom:16 }}>
            <input type="email" value={regEmail} onChange={e=>{setRegEmail(e.target.value); setEmailValid(null); setEmailErrorMsg(''); resetMessages();}} onBlur={()=> regEmail && checkEmailExists(regEmail)} placeholder="you@example.com" style={{ width:'100%', padding:10, paddingRight:40, border: emailValid === false ? '2px solid #ef4444' : emailValid === true ? '2px solid #22c55e' : '1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' }} required />
            {emailValid === true && <span style={{position:'absolute',right:12,top:10,color:'#22c55e',fontSize:20}}>✓</span>}
            {emailValid === false && <span style={{position:'absolute',right:12,top:10,color:'#ef4444',fontSize:20}}>✗</span>}
          </div>
          {emailValid === true && <div style={{color:'#22c55e',fontSize:12,marginTop:-12,marginBottom:12}}>✓ Email available</div>}
          {emailValid === false && <div style={{color:'#ef4444',fontSize:12,marginTop:-12,marginBottom:12}}>✗ {emailErrorMsg}</div>}

          <label style={{ display:'block', fontSize:13, opacity:0.9, marginBottom:4 }}>Phone Number *</label>
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            <select value={regCountryCode} onChange={e=>setRegCountryCode(e.target.value)} style={{ padding:10, border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', background:'#fff', minWidth:110 }} required>
              <option value="IN">🇮🇳 +91</option>
              <option value="US">🇺🇸 +1</option>
              <option value="GB">🇬🇧 +44</option>
              <option value="CA">🇨🇦 +1</option>
              <option value="AU">🇦🇺 +61</option>
              <option value="NZ">🇳🇿 +64</option>
              <option value="SG">🇸🇬 +65</option>
              <option value="AE">🇦🇪 +971</option>
              <option value="MY">🇲🇾 +60</option>
              <option value="ZA">🇿🇦 +27</option>
              <option value="OTHER">🌍 Other</option>
            </select>
            <div style={{ position:'relative', flex:1 }}>
              <input type="tel" value={regPhone} onChange={e=>{setRegPhone(e.target.value); setPhoneValid(null); setPhoneErrorMsg(''); resetMessages();}} onBlur={()=> regPhone && checkPhoneExists(regPhone)} placeholder="9876543210" style={{ width:'100%', padding:10, paddingRight:40, border: phoneValid === false ? '2px solid #ef4444' : phoneValid === true ? '2px solid #22c55e' : '1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' }} required />
              {phoneValid === true && <span style={{position:'absolute',right:12,top:10,color:'#22c55e',fontSize:20}}>✓</span>}
              {phoneValid === false && <span style={{position:'absolute',right:12,top:10,color:'#ef4444',fontSize:20}}>✗</span>}
            </div>
          </div>
          {phoneValid === true && <div style={{color:'#22c55e',fontSize:12,marginTop:-12,marginBottom:12}}>✓ Phone available</div>}
          {phoneValid === false && <div style={{color:'#ef4444',fontSize:12,marginTop:-12,marginBottom:12}}>✗ {phoneErrorMsg}</div>}

          <button type="submit" disabled={busy || !regName || !regEmail || !regPhone || !regCountryCode || emailValid === false || phoneValid === false} style={{ width:'100%', padding:12, borderRadius:8, border:'none', background: busy || emailValid === false || phoneValid === false ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color:'#fff', fontWeight:'bold', cursor: busy || emailValid === false || phoneValid === false ? 'not-allowed' : 'pointer', fontSize:14 }}>Continue</button>
        </form>
      )}

      {/* PLAN SELECTION + PAYMENT + MAGIC LINK */}
      {mode === 'signup' && regCompleted && !magicLinkSent && (
        <div style={{padding:20}}>
          {/* User Details Summary */}
          <div style={{background:'#f0f9ff',padding:12,borderRadius:8,marginBottom:20,fontSize:13}}>
            <strong>{regName}</strong> • {regEmail} • {regPhone}<br/>
            Region: {regCountryCode === 'IN' ? '🇮🇳 India (INR)' : '🌍 International (USD)'}
          </div>

          {/* Plan Selection */}
          <h3 style={{margin:0,marginBottom:12,fontSize:16,color:'#667eea'}}>1. Choose Your Plan</h3>
          
          {/* Billing Cycle Toggle */}
          <div style={{display:'flex',gap:8,marginBottom:16,justifyContent:'center'}}>
            <button 
              onClick={()=>setBillingCycle('monthly')} 
              style={{padding:'8px 16px',borderRadius:8,border:'1px solid #ddd',background:billingCycle==='monthly'?'#667eea':'#fff',color:billingCycle==='monthly'?'#fff':'#333',cursor:'pointer',fontSize:13,fontWeight:billingCycle==='monthly'?'bold':'normal'}}>
              Monthly
            </button>
            <button 
              onClick={()=>setBillingCycle('yearly')} 
              style={{padding:'8px 16px',borderRadius:8,border:'1px solid #ddd',background:billingCycle==='yearly'?'#667eea':'#fff',color:billingCycle==='yearly'?'#fff':'#333',cursor:'pointer',fontSize:13,fontWeight:billingCycle==='yearly'?'bold':'normal'}}>
              Yearly <span style={{fontSize:11,opacity:0.9}}>(Save 17%)</span>
            </button>
          </div>

          {/* Plan Cards */}
          <div style={{display:'flex',gap:12,marginBottom:20}}>
            {/* Silver Plan */}
            <div 
              onClick={()=>setSelectedPlan('silver')} 
              style={{flex:1,border:selectedPlan==='silver'?'3px solid #667eea':'2px solid #ddd',borderRadius:12,padding:16,cursor:'pointer',background:selectedPlan==='silver'?'#f0f9ff':'#fff',transition:'all 0.2s'}}>
              <div style={{fontSize:18,fontWeight:'bold',marginBottom:4}}>Silver</div>
              <div style={{fontSize:24,fontWeight:'bold',color:'#667eea',marginBottom:8}}>
                {regCountryCode === 'IN' 
                  ? (billingCycle === 'monthly' ? '₹299' : '₹2,990')
                  : (billingCycle === 'monthly' ? '$4.99' : '$49.99')}
                <span style={{fontSize:13,fontWeight:'normal',color:'#666'}}>/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
              </div>
              <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>
                ✓ Unlimited charts<br/>
                ✓ 3 AI chat queries/month<br/>
                ✓ Family member access<br/>
                ✓ Yogas & predictions
              </div>
            </div>

            {/* Gold Plan */}
            <div 
              onClick={()=>setSelectedPlan('gold')} 
              style={{flex:1,border:selectedPlan==='gold'?'3px solid #f59e0b':'2px solid #ddd',borderRadius:12,padding:16,cursor:'pointer',background:selectedPlan==='gold'?'#fffbeb':'#fff',transition:'all 0.2s',position:'relative'}}>
              <div style={{position:'absolute',top:-10,right:10,background:'#f59e0b',color:'#fff',padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:'bold'}}>POPULAR</div>
              <div style={{fontSize:18,fontWeight:'bold',marginBottom:4}}>Gold</div>
              <div style={{fontSize:24,fontWeight:'bold',color:'#f59e0b',marginBottom:8}}>
                {regCountryCode === 'IN' 
                  ? (billingCycle === 'monthly' ? '₹699' : '₹6,990')
                  : (billingCycle === 'monthly' ? '$9.99' : '$99.99')}
                <span style={{fontSize:13,fontWeight:'normal',color:'#666'}}>/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
              </div>
              <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>
                ✓ All Silver features<br/>
                ✓ 6 AI chat queries/month<br/>
                ✓ Email support<br/>
                ✓ Astrological reminders
              </div>
            </div>
          </div>

          {/* Payment Section */}
          {selectedPlan && (
            <>
              <h3 style={{margin:'20px 0 12px 0',fontSize:16,color:'#667eea'}}>2. Complete Payment</h3>
              <div style={{background:'#f9fafb',padding:16,borderRadius:8,marginBottom:16}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                  <span style={{fontSize:14}}>Plan: <strong>{selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} - {billingCycle}</strong></span>
                  <span style={{fontSize:16,fontWeight:'bold',color:'#667eea'}}>
                    {regCountryCode === 'IN' 
                      ? (selectedPlan === 'silver' 
                          ? (billingCycle === 'monthly' ? '₹299' : '₹2,990')
                          : (billingCycle === 'monthly' ? '₹699' : '₹6,990'))
                      : (selectedPlan === 'silver' 
                          ? (billingCycle === 'monthly' ? '$4.99' : '$49.99')
                          : (billingCycle === 'monthly' ? '$9.99' : '$99.99'))}
                  </span>
                </div>
                <div style={{fontSize:12,color:'#666',marginTop:8}}>
                  🔒 Payment will be captured now. You'll get immediate access to your 7-day free trial.<br/>
                  💰 Full refund available if you cancel within 7 days.
                </div>
              </div>

              {!paymentCompleted ? (
                <button 
                  onClick={handlePayment}
                  disabled={paymentProcessing}
                  style={{width:'100%',padding:14,borderRadius:8,border:'none',background:paymentProcessing?'#ccc':'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',color:'#fff',fontWeight:'bold',cursor:paymentProcessing?'wait':'pointer',fontSize:15,marginBottom:12}}>
                  {paymentProcessing ? 'Processing...' : `💳 Pay ${regCountryCode === 'IN' 
                    ? (selectedPlan === 'silver' 
                        ? (billingCycle === 'monthly' ? '₹299' : '₹2,990')
                        : (billingCycle === 'monthly' ? '₹699' : '₹6,990'))
                    : (selectedPlan === 'silver' 
                        ? (billingCycle === 'monthly' ? '$4.99' : '$49.99')
                        : (billingCycle === 'monthly' ? '$9.99' : '$99.99'))} - Start Trial`}
                </button>
              ) : (
                <div style={{background:'#dcfce7',border:'2px solid #22c55e',padding:16,borderRadius:8,marginBottom:12,textAlign:'center'}}>
                  <div style={{fontSize:32,marginBottom:8}}>✓</div>
                  <div style={{fontSize:14,fontWeight:'bold',color:'#15803d',marginBottom:4}}>Payment Successful!</div>
                  <div style={{fontSize:12,color:'#166534'}}>Your trial is ready. Click below to activate.</div>
                </div>
              )}
            </>
          )}

          {/* Magic Link Button - Only active after payment */}
          <h3 style={{margin:'20px 0 12px 0',fontSize:16,color:'#667eea'}}>3. Activate Your Trial</h3>
          <button 
            onClick={handleSendMagicLink}
            disabled={!paymentCompleted || busy}
            style={{width:'100%',padding:14,borderRadius:8,border:'none',background:paymentCompleted&&!busy?'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)':'#ccc',color:'#fff',fontWeight:'bold',cursor:paymentCompleted&&!busy?'pointer':'not-allowed',fontSize:15,marginBottom:16}}>
            {busy ? 'Sending...' : (paymentCompleted ? '📧 Send Activation Link' : '🔒 Complete Payment First')}
          </button>

          <button onClick={()=>{setRegCompleted(false); setSelectedPlan(null); setPaymentCompleted(false); setMsg(null);}} style={{width:'100%',padding:'10px',borderRadius:8,border:'1px solid #ddd',background:'#fff',cursor:'pointer',fontSize:13,color:'#666'}}>← Back to Registration</button>
        </div>
      )}

      {/* MAGIC LINK SENT */}
      {mode === 'signup' && magicLinkSent && (
        <div style={{textAlign:'center',padding:24}}>
          <div style={{fontSize:48,marginBottom:16}}>📧</div>
          <h3 style={{margin:0,marginBottom:8,color:'#667eea'}}>Check Your Email!</h3>
          <p style={{color:'#666',fontSize:14,lineHeight:1.6,marginBottom:16}}>We've sent a magic link to <strong>{regEmail}</strong></p>
          <div style={{background:'#f0f9ff',padding:16,borderRadius:8,marginBottom:16,fontSize:13,lineHeight:1.6}}>
            <strong>Next steps:</strong><br/>
            1. Check your email inbox<br/>
            2. Click the login link<br/>
            3. Your 7-day free trial will be activated automatically!
          </div>
          <button onClick={()=>{setMagicLinkSent(false); setRegName(''); setRegEmail(''); setRegPhone(''); setEmailValid(null); setPhoneValid(null); setMsg(null);}} style={{padding:'10px 20px',borderRadius:8,border:'1px solid #ddd',background:'#fff',cursor:'pointer',fontSize:14}}>Register Another User</button>
        </div>
      )}

      {/* OLD PLAN SELECTION FOR TRIAL MODE - HIDDEN (replaced by unified payment choice screen) */}
      {false && mode === 'trial' && showPlanSelection && !magicLinkSent && (
        <div style={{padding:20}}>
          <h3 style={{margin:'0 0 16px 0',fontSize:20,color:'#667eea',textAlign:'center'}}>
            Choose Your Plan
          </h3>
          
          {/* User Details Summary */}
          <div style={{background:'#f0f9ff',padding:12,borderRadius:8,marginBottom:24,fontSize:13}}>
            <strong>{regName}</strong> • {regEmail} • {regPhone}<br/>
            Region: {regCountryCode === 'IN' ? '🇮🇳 India (INR)' : '🌍 International (USD)'}
          </div>

          {/* Free Trial Option - Highlighted */}
          <div 
            onClick={() => {
              setUseFreeTrial(true);
              setSelectedPlan(null);
              sendMagicLinkForRegistration();
            }}
            style={{
              background:'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              border: useFreeTrial ? '4px solid #22c55e' : '3px solid #22c55e',
              borderRadius:16,
              padding:24,
              marginBottom:20,
              cursor:'pointer',
              transition:'all 0.3s',
              boxShadow: useFreeTrial ? '0 8px 24px rgba(34,197,94,0.3)' : '0 4px 12px rgba(34,197,94,0.2)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <div style={{fontSize:24,fontWeight:'bold',color:'#16a34a'}}>
                🎁 Start with Free Trial
              </div>
              <div style={{fontSize:16,fontWeight:'bold',color:'#16a34a'}}>
                ₹0 / 7 days
              </div>
            </div>
            <div style={{fontSize:14,color:'#166534',lineHeight:1.6,marginBottom:12}}>
              ✓ Immediate access - Start using now<br/>
              ✓ 1 complete chart calculation<br/>
              ✓ 3 AI chat questions<br/>
              ✓ All predictions, yogas & divisional charts<br/>
              ✓ Upgrade anytime during trial
            </div>
            <div style={{fontSize:13,fontWeight:'bold',color:'#15803d',textAlign:'center',padding:8,background:'rgba(255,255,255,0.5)',borderRadius:8}}>
              👉 Recommended for first-time users
            </div>
          </div>

          {/* Paid Plans Section */}
          <div style={{fontSize:14,color:'#666',textAlign:'center',marginBottom:16,fontWeight:500}}>
            OR choose a paid plan (payment link sent via email)
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:24}}>
            {/* Silver Plan */}
            <div 
              onClick={() => {
                setUseFreeTrial(false);
                setSelectedPlan('silver');
                sendMagicLinkForRegistration();
              }}
              style={{
                background:'linear-gradient(135deg, #f0f0f0 0%, #f8f8f8 100%)',
                border: selectedPlan === 'silver' ? '4px solid #C0C0C0' : '2px solid #ddd',
                borderRadius:12,
                padding:20,
                cursor:'pointer',
                transition:'all 0.3s',
                textAlign:'center'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{fontSize:20,fontWeight:'bold',color:'#808080',marginBottom:8}}>
                Silver
              </div>
              <div style={{fontSize:24,fontWeight:'bold',marginBottom:12}}>
                ₹2,991/yr
              </div>
              <div style={{fontSize:12,color:'#666',lineHeight:1.5}}>
                ✓ Self + Spouse + 2 family<br/>
                ✓ 3 AI chats/month
              </div>
            </div>

            {/* Gold Plan */}
            <div 
              onClick={() => {
                setUseFreeTrial(false);
                setSelectedPlan('gold');
                sendMagicLinkForRegistration();
              }}
              style={{
                background:'linear-gradient(135deg, #fff5e6 0%, #fffbf0 100%)',
                border: selectedPlan === 'gold' ? '4px solid #DAA520' : '2px solid #DAA520',
                borderRadius:12,
                padding:20,
                cursor:'pointer',
                transition:'all 0.3s',
                textAlign:'center'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{fontSize:20,fontWeight:'bold',color:'#DAA520',marginBottom:8}}>
                Gold
              </div>
              <div style={{fontSize:24,fontWeight:'bold',marginBottom:12}}>
                ₹6,996/yr
              </div>
              <div style={{fontSize:12,color:'#666',lineHeight:1.5}}>
                ✓ Self + Spouse + 4 family<br/>
                ✓ 6 AI chats/month<br/>
                ✓ Marriage Compatibility
              </div>
            </div>
          </div>

          <button 
            onClick={() => {
              setShowPlanSelection(false);
              setRegCompleted(false);
              setMsg(null);
            }} 
            style={{
              width:'100%',
              padding:'10px',
              borderRadius:8,
              border:'1px solid #ddd',
              background:'#fff',
              cursor:'pointer',
              fontSize:13,
              color:'#666'
            }}
          >
            ← Back to Registration
          </button>
        </div>
      )}

      {/* REGISTRATION FORM FOR TRIAL MODE (via Floating Button) - HIDDEN (replaced by unified registration form) */}
      {false && mode === 'trial' && regCompleted === false && !showPlanSelection && !freeTrialSent && !magicLinkSent && (
        <div style={{padding:20}}>
          <h3 style={{margin:'0 0 16px 0',fontSize:18,color:'#16a34a',textAlign:'center'}}>
            Complete Your Registration
          </h3>
          
          <form onSubmit={handleRegisterSubmit}>
            <label style={{ display:'block', fontSize:13, opacity:0.9, marginBottom:4 }}>Full Name *</label>
            <input 
              type="text" 
              value={regName} 
              onChange={e=>setRegName(e.target.value)} 
              placeholder="John Doe" 
              style={{ width:'100%', margin:'0 0 16px 0', padding:12, border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' }} 
              required 
            />
            
            <label style={{ display:'block', fontSize:13, opacity:0.9, marginBottom:4 }}>Email *</label>
            <div style={{ position:'relative', marginBottom:16 }}>
              <input 
                type="email" 
                value={regEmail} 
                onChange={e=>{setRegEmail(e.target.value); setEmailValid(null); setEmailErrorMsg(''); resetMessages();}} 
                onBlur={()=> regEmail && checkEmailExists(regEmail)} 
                placeholder="you@example.com" 
                style={{ 
                  width:'100%', 
                  padding:12, 
                  paddingRight:40, 
                  border: emailValid === false ? '2px solid #ef4444' : emailValid === true ? '2px solid #22c55e' : '1px solid #ddd', 
                  borderRadius:8, 
                  fontSize:14, 
                  boxSizing:'border-box' 
                }} 
                required 
              />
              {emailValid === true && <span style={{position:'absolute',right:12,top:12,color:'#22c55e',fontSize:20}}>✓</span>}
              {emailValid === false && <span style={{position:'absolute',right:12,top:12,color:'#ef4444',fontSize:20}}>✗</span>}
            </div>
            {emailValid === true && <div style={{color:'#22c55e',fontSize:12,marginTop:-12,marginBottom:12}}>✓ Email available</div>}
            {emailValid === false && <div style={{color:'#ef4444',fontSize:12,marginTop:-12,marginBottom:12}}>✗ {emailErrorMsg}</div>}

            <label style={{ display:'block', fontSize:13, opacity:0.9, marginBottom:4 }}>Phone Number *</label>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              <select 
                value={regCountryCode} 
                onChange={e=>setRegCountryCode(e.target.value)} 
                style={{ padding:12, border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', background:'#fff', minWidth:110 }} 
                required
              >
                <option value="IN">🇮🇳 +91</option>
                <option value="US">🇺🇸 +1</option>
                <option value="GB">🇬🇧 +44</option>
                <option value="CA">🇨🇦 +1</option>
                <option value="AU">🇦🇺 +61</option>
                <option value="NZ">🇳🇿 +64</option>
                <option value="SG">🇸🇬 +65</option>
                <option value="AE">🇦🇪 +971</option>
                <option value="MY">🇲🇾 +60</option>
                <option value="ZA">🇿🇦 +27</option>
                <option value="OTHER">🌍 Other</option>
              </select>
              <div style={{ position:'relative', flex:1 }}>
                <input 
                  type="tel" 
                  value={regPhone} 
                  onChange={e=>{setRegPhone(e.target.value); setPhoneValid(null); setPhoneErrorMsg(''); resetMessages();}} 
                  onBlur={()=> regPhone && checkPhoneExists(regPhone)} 
                  placeholder="9876543210" 
                  style={{ 
                    width:'100%', 
                    padding:12, 
                    paddingRight:40, 
                    border: phoneValid === false ? '2px solid #ef4444' : phoneValid === true ? '2px solid #22c55e' : '1px solid #ddd', 
                    borderRadius:8, 
                    fontSize:14, 
                    boxSizing:'border-box' 
                  }} 
                  required 
                />
                {phoneValid === true && <span style={{position:'absolute',right:12,top:12,color:'#22c55e',fontSize:20}}>✓</span>}
                {phoneValid === false && <span style={{position:'absolute',right:12,top:12,color:'#ef4444',fontSize:20}}>✗</span>}
              </div>
            </div>
            {phoneValid === true && <div style={{color:'#22c55e',fontSize:12,marginTop:-12,marginBottom:12}}>✓ Phone available</div>}
            {phoneValid === false && <div style={{color:'#ef4444',fontSize:12,marginTop:-12,marginBottom:12}}>✗ {phoneErrorMsg}</div>}

            <button 
              type="submit" 
              disabled={busy || !regName || !regEmail || !regPhone || !regCountryCode || emailValid === false || phoneValid === false} 
              style={{ 
                width:'100%', 
                padding:14, 
                borderRadius:8, 
                border:'none', 
                background: busy || emailValid === false || phoneValid === false ? '#ccc' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', 
                color:'#fff', 
                fontWeight:'bold', 
                cursor: busy || emailValid === false || phoneValid === false ? 'not-allowed' : 'pointer', 
                fontSize:15,
                marginTop: 8
              }}
            >
              {busy ? 'Processing...' : 'Continue to Plan Selection'}
            </button>
          </form>
          
          <div style={{
            marginTop: 20,
            padding: 16,
            background: '#f0fdf4',
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.6,
            textAlign: 'center'
          }}>
            💡 Next step: Choose between <strong>Free Trial</strong> (immediate access) or select a paid plan
          </div>
        </div>
      )}

      {/* OLD PLAN SELECTION FOR TRIAL MODE (DUPLICATE) - HIDDEN (replaced by unified payment choice screen) */}
      {false && mode === 'trial' && showPlanSelection && !magicLinkSent && (
        <div style={{padding:20}}>
          <h3 style={{margin:'0 0 16px 0',fontSize:20,color:'#667eea',textAlign:'center'}}>
            Choose Your Plan
          </h3>
          
          {/* User Details Summary */}
          <div style={{background:'#f0f9ff',padding:12,borderRadius:8,marginBottom:24,fontSize:13}}>
            <strong>{regName}</strong> • {regEmail} • {regPhone}<br/>
            Region: {regCountryCode === 'IN' ? '🇮🇳 India (INR)' : '🌍 International (USD)'}
          </div>

          {/* Free Trial Option - Highlighted */}
          <div 
            onClick={() => {
              setUseFreeTrial(true);
              setSelectedPlan(null);
              sendMagicLinkForRegistration();
            }}
            style={{
              background:'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              border: useFreeTrial ? '4px solid #22c55e' : '3px solid #22c55e',
              borderRadius:16,
              padding:24,
              marginBottom:20,
              cursor:'pointer',
              transition:'all 0.3s',
              boxShadow: useFreeTrial ? '0 8px 24px rgba(34,197,94,0.3)' : '0 4px 12px rgba(34,197,94,0.2)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <div style={{fontSize:24,fontWeight:'bold',color:'#16a34a'}}>
                🎁 Start with Free Trial
              </div>
              <div style={{fontSize:16,fontWeight:'bold',color:'#16a34a'}}>
                ₹0 / 7 days
              </div>
            </div>
            <div style={{fontSize:14,color:'#166534',lineHeight:1.6,marginBottom:12}}>
              ✓ Immediate access - Start using now<br/>
              ✓ 1 complete chart calculation<br/>
              ✓ 3 AI chat questions<br/>
              ✓ All predictions, yogas & divisional charts<br/>
              ✓ Upgrade anytime during trial
            </div>
            <div style={{fontSize:13,fontWeight:'bold',color:'#15803d',textAlign:'center',padding:8,background:'rgba(255,255,255,0.5)',borderRadius:8}}>
              👉 Recommended for first-time users
            </div>
          </div>

          {/* Paid Plans Section */}
          <div style={{fontSize:14,color:'#666',textAlign:'center',marginBottom:16,fontWeight:500}}>
            OR choose a paid plan (payment link sent via email)
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:24}}>
            {/* Silver Plan */}
            <div 
              onClick={() => {
                setUseFreeTrial(false);
                setSelectedPlan('silver');
                sendMagicLinkForRegistration();
              }}
              style={{
                background:'linear-gradient(135deg, #f0f0f0 0%, #f8f8f8 100%)',
                border: selectedPlan === 'silver' ? '4px solid #C0C0C0' : '2px solid #ddd',
                borderRadius:12,
                padding:20,
                cursor:'pointer',
                transition:'all 0.3s',
                textAlign:'center'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{fontSize:20,fontWeight:'bold',color:'#808080',marginBottom:8}}>
                Silver
              </div>
              <div style={{fontSize:24,fontWeight:'bold',marginBottom:12}}>
                ₹2,991/yr
              </div>
              <div style={{fontSize:12,color:'#666',lineHeight:1.5}}>
                ✓ Self + Spouse + 2 family<br/>
                ✓ 3 AI chats/month
              </div>
            </div>

            {/* Gold Plan */}
            <div 
              onClick={() => {
                setUseFreeTrial(false);
                setSelectedPlan('gold');
                sendMagicLinkForRegistration();
              }}
              style={{
                background:'linear-gradient(135deg, #fff5e6 0%, #fffbf0 100%)',
                border: selectedPlan === 'gold' ? '4px solid #DAA520' : '2px solid #DAA520',
                borderRadius:12,
                padding:20,
                cursor:'pointer',
                transition:'all 0.3s',
                textAlign:'center'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{fontSize:20,fontWeight:'bold',color:'#DAA520',marginBottom:8}}>
                Gold
              </div>
              <div style={{fontSize:24,fontWeight:'bold',marginBottom:12}}>
                ₹6,996/yr
              </div>
              <div style={{fontSize:12,color:'#666',lineHeight:1.5}}>
                ✓ Self + Spouse + 4 family<br/>
                ✓ 6 AI chats/month<br/>
                ✓ Marriage Compatibility
              </div>
            </div>
          </div>

          <button 
            onClick={() => {
              setShowPlanSelection(false);
              setRegCompleted(false);
              setMsg(null);
            }} 
            style={{
              width:'100%',
              padding:'10px',
              borderRadius:8,
              border:'1px solid #ddd',
              background:'#fff',
              cursor:'pointer',
              fontSize:13,
              color:'#666'
            }}
          >
            ← Back to Registration
          </button>
        </div>
      )}

      {/* OLD FREE TRIAL TAB - HIDDEN (replaced by unified PLANS tab) */}
      {false && mode === 'trial' && !freeTrialSent && !showPlanSelection && !regCompleted && (
        <div>
          <div style={{background:'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',padding:20,borderRadius:12,marginBottom:20,border:'2px solid #22c55e'}}>
            <h3 style={{margin:0,marginBottom:12,fontSize:18,color:'#16a34a',display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:24}}>🎁</span> Start Your Free Trial
            </h3>
            <p style={{margin:0,fontSize:14,color:'#166534',lineHeight:1.6}}>
              Get <strong>7 days</strong> of free access with:<br/>
              ✓ 1 complete chart calculation<br/>
              ✓ 3 AI chat questions<br/>
              ✓ All predictions, yogas & divisional charts<br/>
              ✓ No credit card required
            </p>
          </div>
          
          {/* Google OAuth Sign-In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={busy}
            style={{
              width:'100%',
              padding:14,
              borderRadius:8,
              border:'1px solid #dadce0',
              background:'#fff',
              color:'#3c4043',
              fontWeight:'500',
              cursor: busy ? 'not-allowed' : 'pointer',
              fontSize:15,
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              gap:12,
              marginBottom:20,
              transition:'background 0.2s, box-shadow 0.2s',
              boxShadow:'0 1px 2px rgba(0,0,0,0.1)',
            }}
            onMouseEnter={(e) => {
              if (!busy) {
                e.currentTarget.style.background = '#f8f9fa';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
              <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853"/>
              <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
            </svg>
            {busy ? 'Signing in...' : 'Continue with Google'}
          </button>
          
          <div style={{fontSize:11,color:'#64748b',textAlign:'center',marginBottom:16,lineHeight:1.4}}>
            By continuing with Google, you agree to share your email address for registration and login.
          </div>
          
          {/* OR divider */}
          <div style={{display:'flex',alignItems:'center',gap:12,margin:'20px 0'}}>
            <div style={{flex:1,height:1,background:'#ddd'}}></div>
            <span style={{color:'#666',fontSize:13,fontWeight:'500'}}>OR</span>
            <div style={{flex:1,height:1,background:'#ddd'}}></div>
          </div>
          
          <form onSubmit={handleFreeTrialSubmit}>
            <label style={{ display:'block', fontSize:13, opacity:0.9, marginBottom:6 }}>Enter your email to start</label>
            <input 
              type="email" 
              value={freeTrialEmail} 
              onChange={e=>setFreeTrialEmail(e.target.value)} 
              placeholder="you@example.com"
              style={{ width:'100%', margin:'0 0 16px 0', padding:12, border:'2px solid #22c55e', borderRadius:8, fontSize:14, boxSizing:'border-box' }}
              required 
            />
            
            <button 
              type="submit" 
              disabled={busy || !freeTrialEmail} 
              style={{ width:'100%', padding:14, borderRadius:8, border:'none', background: busy || !freeTrialEmail ? '#ccc' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color:'#fff', fontWeight:'bold', cursor: busy || !freeTrialEmail ? 'not-allowed' : 'pointer', fontSize:15, boxShadow: busy || !freeTrialEmail ? 'none' : '0 4px 12px rgba(34,197,94,0.3)' }}>
              {busy ? 'Sending...' : '📧 Send Magic Link'}
            </button>
          </form>
          
          <div style={{marginTop:16,padding:12,background:'#f8fafc',borderRadius:8,fontSize:12,color:'#64748b',lineHeight:1.6}}>
            📧 We'll send you a magic link. Click it to activate your account instantly.<br/>
            ⏱️ Email may take a few minutes. Check spam folder if not received.<br/>
            💡 Trial lasts 7 days. Upgrade anytime to unlock unlimited charts & more AI queries.<br/>
            <br/>
            <strong style={{color:'#475569'}}>Need immediate access?</strong> Contact admin for instant account creation (no email wait).
          </div>
        </div>
      )}

      {mode === 'trial' && freeTrialSent && (
        <div style={{textAlign:'center',padding:40}}>
          <div style={{fontSize:60,marginBottom:16}}>📬</div>
          <h3 style={{margin:0,marginBottom:12,fontSize:20,color:'#16a34a'}}>Check Your Email!</h3>
          <p style={{color:'#666',fontSize:14,lineHeight:1.8,marginBottom:20}}>
            We've sent a magic link to:<br/>
            <strong style={{color:'#16a34a',fontSize:16}}>{freeTrialEmail}</strong>
          </p>
          <div style={{background:'#f0fdf4',padding:16,borderRadius:8,marginBottom:20,fontSize:13,color:'#166534',lineHeight:1.8}}>
            <strong>Next steps:</strong><br/>
            1. Check your inbox (and spam folder)<br/>
            2. Click the magic link in the email<br/>
            3. Your 7-day free trial activates automatically!
          </div>
          <button 
            onClick={()=>{setFreeTrialSent(false); setFreeTrialEmail(''); setMsg(null);}} 
            style={{padding:'10px 20px',borderRadius:8,border:'1px solid #ddd',background:'#fff',cursor:'pointer',fontSize:14}}>
            Try Another Email
          </button>
        </div>
      )}

      {/* LOGIN TAB */}
      {mode === 'login' && (() => {
        // Derive plan display from subscription info
        const tierLabels: Record<string, string> = {
          trial: 'Free Trial', free_trial: 'Free Trial',
          silver: 'Silver Plan', gold: 'Gold Plan',
          basic: 'Basic Plan', premium: 'Premium Plan',
        };
        const tierLabel = loginUserInfo?.subscription
          ? (tierLabels[loginUserInfo.subscription.tier_key] || loginUserInfo.subscription.tier_key)
          : null;
        const subStatus = loginUserInfo?.subscription?.status ?? null;
        const isActiveUser = loginUserInfo?.exists &&
          (loginUserInfo.isAdmin || subStatus === 'active' || subStatus === 'trial');
        // User exists but plan expired/cancelled or never activated
        const isInactiveUser = loginUserInfo?.exists && !isActiveUser;

        const statusBadgeStyle = (active: boolean): React.CSSProperties => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 12px',
          borderRadius: 20,
          fontSize: 13,
          fontWeight: 600,
          background: active ? '#f0fdf4' : loginUserInfo?.exists ? '#fffbeb' : '#fef2f2',
          color: active ? '#16a34a' : loginUserInfo?.exists ? '#92400e' : '#dc2626',
          border: `1px solid ${active ? '#bbf7d0' : loginUserInfo?.exists ? '#fcd34d' : '#fecaca'}`,
        });

        return (
          <div>
            {/* OTP expired / auth error banner */}
            {initialError && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: '12px 16px',
                marginBottom: 20,
                fontSize: 13,
                color: '#dc2626',
                lineHeight: 1.6,
              }}>
                ⚠️ {initialError}
              </div>
            )}

            {/* Step 1: Email input (before check) OR locked pill (after check) */}
            {!loginMagicSent && (
              <>
                {!loginUserInfo ? (
                  <>
                    <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 6, fontWeight: 500 }}>
                      Enter your email to continue
                    </label>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                      <input
                        type="email"
                        value={email}
                        onChange={e => { setEmail(e.target.value); setMsg(null); }}
                        onKeyDown={e => e.key === 'Enter' && checkLoginEmail()}
                        placeholder="you@example.com"
                        style={{ flex: 1, padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14 }}
                      />
                      <button
                        onClick={checkLoginEmail}
                        disabled={loginChecking || !email}
                        style={{
                          padding: '10px 16px',
                          borderRadius: 8,
                          border: 'none',
                          background: loginChecking || !email ? '#e2e8f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: loginChecking || !email ? '#94a3b8' : '#fff',
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: loginChecking || !email ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {loginChecking ? '…' : 'Continue →'}
                      </button>
                    </div>
                  </>
                ) : (
                  /* After check: locked email pill with Change link */
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 16, padding: '10px 14px',
                    background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
                  }}>
                    <span style={{ fontSize: 14, color: '#334155' }}>📧 {email}</span>
                    <button
                      onClick={() => { setLoginUserInfo(null); setMsg(null); }}
                      style={{ background: 'none', border: 'none', color: '#667eea', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0 }}
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* Status badge after email check */}
                {loginUserInfo && (
                  <div style={{ marginBottom: 16 }}>
                    {!loginUserInfo.exists ? (
                      <div style={statusBadgeStyle(false)}>
                        ✕ No account found for this email
                      </div>
                    ) : (
                      <div style={statusBadgeStyle(!!isActiveUser)}>
                        {isActiveUser ? '✓' : '⚠'}
                        {loginUserInfo.isAdmin ? ' Admin Account' : (tierLabel
                          ? ` ${tierLabel} · ${subStatus === 'trial' ? 'Trial Active' : subStatus === 'active' ? 'Active' : subStatus ? subStatus.charAt(0).toUpperCase() + subStatus.slice(1) : 'No Active Plan'}`
                          : ' Account found · No active plan')}
                      </div>
                    )}

                    {/* Not found: guide to register */}
                    {!loginUserInfo.exists && (
                      <div style={{ marginTop: 12, fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                        No account exists with this email. To get started,{' '}
                        <button
                          onClick={() => { setMode('trial'); setLoginUserInfo(null); setMsg(null); }}
                          style={{ background: 'none', border: 'none', color: '#667eea', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 13 }}
                        >
                          choose a plan and register →
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Login options — shown only after a successful email check */}
                {loginUserInfo?.exists && (
                  <>
                    {/* Google OAuth — only for active/trial users */}
                    <button
                      onClick={handleGoogleSignIn}
                      disabled={busy || !isActiveUser}
                      title={isInactiveUser ? 'Your plan has expired. Use the magic link option below to log in and renew.' : ''}
                      style={{
                        width: '100%',
                        padding: 14,
                        borderRadius: 8,
                        border: '1px solid #dadce0',
                        background: isActiveUser ? '#fff' : '#f8fafc',
                        color: isActiveUser ? '#3c4043' : '#94a3b8',
                        fontWeight: '500',
                        cursor: isActiveUser && !busy ? 'pointer' : 'not-allowed',
                        fontSize: 15,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                        marginBottom: 8,
                        boxShadow: isActiveUser ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                        opacity: isActiveUser ? 1 : 0.5,
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                        <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853"/>
                        <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                        <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
                      </svg>
                      {busy ? 'Signing in...' : 'Sign in with Google'}
                    </button>
                    {isInactiveUser && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12, paddingLeft: 4 }}>
                        Google sign-in available for active plans only
                      </div>
                    )}
                    {isActiveUser && (
                      <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center', marginBottom: 16 }}>
                        Quick & secure — no password needed
                      </div>
                    )}

                    {/* OR divider */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
                      <div style={{ flex: 1, height: 1, background: '#ddd' }}></div>
                      <span style={{ color: '#666', fontSize: 13, fontWeight: '500' }}>OR</span>
                      <div style={{ flex: 1, height: 1, background: '#ddd' }}></div>
                    </div>

                    {/* Magic link — available for all existing users */}
                    <button
                      onClick={sendLoginMagicLink}
                      disabled={busy}
                      style={{
                        width: '100%',
                        padding: 12,
                        borderRadius: 8,
                        background: busy ? '#e2e8f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: busy ? '#94a3b8' : '#fff',
                        border: 'none',
                        fontWeight: 'bold',
                        fontSize: 14,
                        cursor: busy ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {busy ? 'Sending…' : '📧 Send Magic Link'}
                    </button>
                    {isInactiveUser && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                        Log in with your magic link to renew your subscription.
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Magic link sent confirmation */}
            {loginMagicSent && (
              <div style={{ textAlign: 'center', padding: 24, background: '#f0f9ff', borderRadius: 8 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
                <h3 style={{ margin: 0, marginBottom: 8, color: '#667eea' }}>Check Your Email!</h3>
                <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
                  We've sent a magic link to <strong>{email}</strong>
                </p>
                <p style={{ color: '#666', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
                  Click the link to log in instantly. The link is valid for 1 hour.
                </p>
                <button
                  onClick={() => { setLoginMagicSent(false); setLoginUserInfo(null); setEmail(''); setMsg(null); }}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 }}
                >
                  Use Different Email
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {msg && <div style={{ marginTop: 10, fontSize: 13, opacity: 0.95 }}>{msg}</div>}
      </div> {/* Close content wrapper */}
    </div>
  );
}

export function SignOutButton() {
  return (
    <button
      onClick={() => supabase.auth.signOut()}
      style={{ padding:'6px 10px', border:'1px solid #ddd', borderRadius:8 }}
    >
      Sign out
    </button>
  );
}
// END FILE: src/components/AuthPanel.tsx
